import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  PlusCircle,
  Wrench,
  Receipt,
  ShieldCheck,
  Bell,
  Settings,
  FileText,
  X,
  Calculator,
  Banknote,
  MessageSquare,
  CalendarDays,
  TrendingUp,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../shadui-components/button';
import { ScrollArea } from '../shadui-components/scroll-area';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const LandlordToolkitSidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const handleBookDemo = () => {
    const subject = encodeURIComponent('Request for RentSmart Landlord Toolkit Demo');
    const body = encodeURIComponent(
      `Hi RentSmart Team,

I'm interested in learning more about the Landlord Toolkit and would like to schedule a demo.

Name: 
Phone: 
Preferred time: 

Please let me know your availability.

Thank you!`
    );
    window.location.href = `mailto:support@rentsmart.ie?subject=${subject}&body=${body}`;
  };

  const navItems = [
    {
      title: 'Dashboard',
      href: '/landlord-toolkit',
      icon: LayoutDashboard,
      exact: true,
    },
    {
      title: 'Messages',
      href: '/landlord-toolkit/messages',
      icon: MessageSquare,
    },
    {
      title: 'Book a Demo With Us',
      icon: CalendarDays,
      action: 'bookDemo',
    },
    {
      title: 'Properties',
      icon: Building2,
      items: [
        {
          title: 'My Property Portfolio',
          href: '/landlord-toolkit/managed-properties',
          icon: Building2,
        },
        {
          title: 'Manage Viewings',
          href: '/landlord-toolkit/manage-viewings',
          icon: CalendarDays,
        },
        {
          title: 'Add Managed Property',
          href: '/landlord-toolkit/add-property',
          icon: PlusCircle,
        },
      ],
    },
    {
      title: 'Operations',
      icon: Wrench,
      items: [
        {
          title: 'Maintenance',
          href: '/landlord-dashboard/maintenance',
          icon: Wrench,
        },
        {
          title: 'Rent Tracker',
          href: '/landlord-toolkit/rent-tracker',
          icon: Banknote,
        },
        {
          title: 'Rent Review Calc',
          href: '/landlord-toolkit/rent-review-calculator',
          icon: Calculator,
        },
        {
          title: 'Home Valuation Tool',
          href: '/home-valuation',
          icon: TrendingUp,
        },
        {
          title: 'Expenses',
          href: '/landlord-toolkit/expenses',
          icon: Receipt,
        },
      ],
    },
    {
      title: 'Compliance',
      icon: ShieldCheck,
      items: [
        {
          title: 'Overview',
          href: '/landlord-toolkit/compliance',
          icon: ShieldCheck,
        },
        {
          title: 'Documentation',
          href: '/landlord-toolkit/docs',
          icon: FileText,
        },
        {
          title: 'Rental Agreements templates',
          href: '/landlord-resources/rental-agreement-templates',
          icon: FileText,
        },
      ],
    },
    {
      title: 'Settings',
      icon: Settings,
      items: [
        {
          title: 'Reminders',
          href: '/landlord-toolkit/reminders',
          icon: Bell,
        },
      ],
    },
  ];

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={onClose} />}

      {/* Sidebar Container */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-white border-r shadow-sm transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:h-screen',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex h-16 items-center justify-between border-b px-4">
            <div className="flex items-center gap-2 font-semibold text-primary">
              <Building2 className="h-6 w-6" />
              <span>Landlord Toolkit</span>
            </div>
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="space-y-1 px-2">
              {navItems.map((item, index) => (
                <div key={index} className="py-2">
                  {item.items ? (
                    <div className="space-y-1">
                      <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {item.title}
                      </div>
                      {item.items.map((subItem) => (
                        <NavLink
                          key={subItem.href}
                          to={subItem.href}
                          onClick={() => window.innerWidth < 1024 && onClose()}
                          className={({ isActive }) =>
                            cn(
                              'group flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground',
                              isActive
                                ? 'bg-primary/10 text-primary hover:bg-primary/15'
                                : 'text-muted-foreground'
                            )
                          }
                        >
                          <subItem.icon className="mr-3 h-4 w-4" />
                          {subItem.title}
                        </NavLink>
                      ))}
                    </div>
                  ) : item.action === 'bookDemo' ? (
                    <button
                      onClick={() => {
                        handleBookDemo();
                        window.innerWidth < 1024 && onClose();
                      }}
                      className={cn(
                        'group flex w-full items-center rounded-lg px-3 py-2.5 text-sm font-semibold transition-all',
                        'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-sm',
                        'hover:from-blue-700 hover:to-blue-800 hover:shadow-md hover:scale-[1.02]',
                        'active:scale-[0.98]'
                      )}
                    >
                      <item.icon className="mr-3 h-4 w-4 animate-pulse" />
                      {item.title}
                    </button>
                  ) : item.href ? (
                    <NavLink
                      to={item.href}
                      end={item.exact}
                      onClick={() => window.innerWidth < 1024 && onClose()}
                      className={({ isActive }) =>
                        cn(
                          'no-underline group flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground',
                          isActive
                            ? 'bg-primary/10 text-primary hover:bg-primary/15'
                            : 'text-muted-foreground'
                        )
                      }
                    >
                      <item.icon className="mr-3 h-4 w-4" />
                      {item.title}
                    </NavLink>
                  ) : null}
                </div>
              ))}
            </nav>
          </ScrollArea>

          {/* Footer */}
          <div className="border-t p-4">
            <div className="rounded-lg bg-blue-50 p-4">
              <h4 className="mb-1 text-sm font-semibold text-blue-900">Need Help?</h4>
              <p className="text-xs text-blue-700">Check our documentation or contact support.</p>
              <Button variant="link" className="mt-2 h-auto p-0 text-xs text-blue-800" asChild>
                <NavLink to="/landlord-toolkit/docs">View Docs</NavLink>
              </Button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default LandlordToolkitSidebar;
